import SAPOMailConfig from '@/SAPOMailConfig';

export {GTMAnalyticsControllerSingleton as default};

/**
 * Class responsible to manage Google Tag Manager Analytics
 *
 * @author      rsimoes <ruben-r-simoes@telecom.pt>
 * @copyright   Copyright (C) Sapo. All rights reserved.
 * @license     Private
 * @since       7.0.0
 */
class GTMAnalyticsController {

    /**
     * Defines if the GTM is connected
     *
     * @private
     *
     * @since 7.0.0
     *
     * @type {Boolean}
     */
    connected = false;

    /**
     * Defines if the GTM is enabled
     *
     * @private
     *
     * @since 7.0.0
     *
     * @type {Boolean}
     */
    enabled = false;

    /**
     * Holds all queued events
     *
     * @private
     *
     * @since 7.0.0
     *
     * @type {Array}
     */
    queue = [];

    /**
     * Holds the iframe GTM node element
     *
     * @private
     *
     * @since 7.0.0
     *
     * @type {Node}
     */
    iframe = null;

    /**
     * Sets the window object
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {Node} iframe GTM Iframe node element
     *
     * @returns {void}
     */
    setIframe(iframe) {
        this.iframe = iframe;
    }

    /**
     * Enables or disables the GTM Analytics
     *
     * Setting this to false will prevent any events from being sent
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {Boolean} enabled True to enable, false to disable
     *
     * @returns {void}
     */
    setEnabled(enabled) {
        this.enabled = enabled;
    }

    /**
     * Sets the connection status
     *
     * When the connection is false, the events will be queued
     * and sent when the connection is established
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {Boolean} connected True if connected, false if not
     *
     * @returns {void}
     */
    setConnected(connected) {
        this.connected = connected;
    }

    /**
     * Posts a virtual page event
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {String} page The page to be tracked
     *
     * @returns {void}
     */
    postVirtualPage(page, from) {
        this.postEvent(
            this.getVirtualPageEvent(page, from),
        );
    }

    /**
     * Posts a tracker link event
     *
     * @public
     *
     * @since 7.0.0
     *
     * @param {String} feature The feature to be tracked
     * @param {String} block   The block to be tracked
     * @param {String} action  The action to be tracked
     * @param {String} detail  Additional details about the event
     *
     * @returns {void}
     */
    postTrackerLink(module, feature, block, action) {
        this.postEvent(
            this.getTrackerLinkEvent(module, feature, block, action)
        );
    }

    /**
     * Posts an event to the GTM
     *
     * If GTM is not enabled, the event will not be sent
     *
     * @private
     *
     * @since 7.0.0
     *
     * @param {Object} event The event to be sent
     *
     * @returns {void}
     */
    postEvent(event) {
        if (this.enabled === false) {
            return;
        }

        if (this.connected === false) {
            this.addEventToQueue(event);
            return;
        }

        this.publish(event);
    }

    /**
     * Adds an event to the queue
     *
     * This is set to private because the events should be sent or queued
     * by calling the postEvent methods
     *
     * @private
     *
     * @since 7.0.0
     *
     * @param {Object} event The event to be added to the queue
     *
     * @returns {void}
     */
    addEventToQueue(event) {
        this.queue.push(event);
    }

    /**
     * Publishes all events in the queue
     *
     * @public
     *
     * @since 7.0.0
     *
     * @returns {void}
     */
    publishQueue() {
        this.queue.forEach((event) => {
            this.publish(event);
        });
    }

    /**
     * Publishes an event to the GTM
     *
     * @private
     *
     * @since 7.0.0
     *
     * @param {Object} event The event to be sent
     */
    publish(event) {
        this.iframe.contentWindow.postMessage(
            {GTM: event},
            '*'
        );
    }

    /**
     * Returns a virtual page event
     *
     * @private
     *
     * @since 7.0.0
     *
     * @param {String} page The page to be tracked
     *
     * @returns {Object}
     */
    getVirtualPageEvent = (page, from) => {
        let path       = page.name.split('-').join('/');
        let page_title = page.name.split('-').join(' ').replace(/\b\w/g, l => l.toUpperCase());
        let referrer   = (from.name) ? '/' + from.name.split('-').join('/') : document.referrer;

        return {
            event               : 'page_view',
            page_location       : window.location.origin + window.location.pathname + path,
            page_url            : '/' + path,
            page_referrer       : referrer,
            page_title          : page_title,
            screen_resolution   : document.documentElement.clientWidth + 'x' + document.documentElement.clientHeight,
            // page_title          : metadata.title,
            platform            : 'browser',
            age                 : SAPOMailConfig.server.analytics.metadata.age_range,
            gender              : SAPOMailConfig.server.analytics.metadata.sex,
        };
    }

    /**
     * Returns a tracker link event
     *
     * @private
     *
     * @since 7.0.0
     *
     * @param {String} module  The module to be tracked
     * @param {String} feature The feature to be tracked
     * @param {String} block   The block to be tracked
     * @param {String} action  The action to be tracked
     *
     * @returns {Object}
     */
    getTrackerLinkEvent = (module, feature, block, action) => {
       let data = {
            event    : 'trackerlink',
            module   : module,
            feature  : feature,
            block    : block,
            action   : action,
       }

        return data;
    }
}

const GTMAnalyticsControllerSingleton = new GTMAnalyticsController;